// App MY INVENTORY — core + module inventory (en ÉCRITURE).
// Elle ne fait que : déclarer son identité, monter les providers du Core, et
// fournir ses onglets. Navigation, pub, pied de version, connexion, espaces,
// profil et IVV viennent du Core ; l'inventaire et les plans viennent du module.
import React from 'react';
import { StatusBar } from 'expo-status-bar';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { CoreProviders, CoquilleApp, EcranFT, EcranProfil, useAuth, useT, useMenuOngletActif, FonctionsProvider, FONCTIONS_CORE, ApprentissageProvider, BarrePratique, SectionApprentissage, SourcesElementsProvider, useElementsPossedes } from '@mop/core';
import { EcranInventaires, EcranPlans, SectionAccesInventaire, FONCTIONS_INVENTAIRE, PRATIQUES_INVENTAIRE } from '@mop/feature-inventory';
// Import CIBLÉ (pas le barrel) : seule la section d'accès des calendriers est
// embarquée — la gouvernance d'espace couvre TOUTES les ressources.
import SectionAccesCalendriers from '@mop/feature-calendar/src/SectionAccesCalendriers';

const APP = { id: 'mop-my-inventory', nom: 'My Inventory', version: '0.9.26.10', scheme: 'mopmyinventory' };
const CATALOGUE = [...FONCTIONS_CORE, ...FONCTIONS_INVENTAIRE];

// Le module est monté en 'edit' : ici, on configure et on ajuste. (MoPCal
// montera les MÊMES écrans en 'readonly'.)
const Inventaires = () => <EcranInventaires mode="edit" ongletCle="Inventaires" />;

// Ce que je POSSEDE, tous types confondus : le quota de jetons est GLOBAL, donc
// My Inventory doit compter les calendriers de l'utilisateur au meme titre que
// ses inventaires — sinon chaque app appliquerait sa propre limite.
// La REQUETE compte autant que la collection : `inventaires` et `plans` ne sont
// lisibles qu'via `acces array-contains` (leurs regles ne savent pas prouver un
// filtre sur `creePar` — la liste entiere serait refusee). Le tri « a moi » se
// fait ensuite cote client.
const COLLECTIONS_ELEMENTS = [
  { cle: 'calendrier', collection: 'calendriers', filtre: ['creePar', '=='],           emoji: '📅' },
  { cle: 'inventaire', collection: 'inventaires', filtre: ['acces', 'array-contains'], emoji: '📦' },
  { cle: 'plan',       collection: 'plans',       filtre: ['acces', 'array-contains'], emoji: '🗺️' },
];
const SourcesElements = ({ children }) => {
  // `pret` / `erreurs` remontent aussi : un ecran ne doit pas confondre « rien
  // possede » avec « pas encore compte » — sinon il autorise une creation de
  // trop pendant le premier affichage.
  const { sources, pret, erreurs } = useElementsPossedes(COLLECTIONS_ELEMENTS);
  return <SourcesElementsProvider sources={sources} pret={pret} erreurs={erreurs}>{children}</SourcesElementsProvider>;
};
const Plans       = () => <EcranPlans mode="edit" ongletCle="Plans" />;

// Le Profil du Core, enrichi de l'accès « qui voit quoi » aux inventaires/plans.
// Le Core ne connaît pas le module → l'app l'injecte, marqué `acces` pour être
// rendu dans la section « Qui voit quoi ».
// IVV n'est PAS un onglet visible : il est réservé aux valideurs (estIVV) et
// accessible par un appui long sur l'onglet Profil (menu déroulant → IVV).
const Profil = () => {
  const { estIVV } = useAuth();
  const t = useT();
  useMenuOngletActif('Profil', [
    { label: t('ft.ivvFaitsTechniques'), icone: '🐞', navigateVers: 'IVV' },
  ], estIVV);
  return (
    <EcranProfil sections={[
      { cle: 'acces-inventaire', titre: t('inventaire.inventairesPlans'), emoji: '📦', acces: true,
        rendu: <SectionAccesInventaire /> },
      // Gouvernance d'ESPACE : accès des calendriers réglables ici aussi.
      { cle: 'acces-calendriers', titre: t('profil.mesCalendriers'), emoji: '📅', acces: true,
        rendu: <SectionAccesCalendriers /> },
      // Tutoriel & Mise en Pratique : mécanique Core, contenu du module.
      { cle: 'apprentissage', titre: t('apprentissage.titre'), emoji: '🎓',
        sousTitre: t('apprentissage.tutoriels') + ' · ' + t('apprentissage.misesEnPratique'),
        rendu: <SectionApprentissage /> },
    ]} />
  );
};

// Onglets construits SOUS les providers (accès à t) — labels traduits.
const Racine = () => {
  const t = useT();
  const onglets = [
    { cle: 'Inventaires', label: t('nav.inventaires'), icone: '📦', composant: Inventaires },
    { cle: 'Plans',       label: t('nav.plans'),       icone: '🗺️', composant: Plans },
    { cle: 'Profil',      label: t('nav.profil'),      icone: '👤', composant: Profil },
    // Écran navigable mais MASQUÉ de la barre (accès IVV réservé, via appui long).
    { cle: 'IVV',         label: t('nav.ivv'),         icone: '🐞', composant: EcranFT, masque: true },
  ];
  return (
    <>
      <CoquilleApp onglets={onglets} avecPub />
      {/* Mise en Pratique en cours : pastille/carte flottante au niveau app. */}
      <BarrePratique />
    </>
  );
};

export default function App() {
  return (
    // GestureHandlerRootView : requis par le swipe des listes (LigneSwipe).
    <GestureHandlerRootView style={{ flex: 1 }}>
      <CoreProviders app={APP} theme="auto">
        <SourcesElements>
        <FonctionsProvider catalogue={CATALOGUE}>
          <ApprentissageProvider pratiques={PRATIQUES_INVENTAIRE}>
            <StatusBar style="auto" />
            <Racine />
          </ApprentissageProvider>
        </FonctionsProvider>
        </SourcesElements>
      </CoreProviders>
    </GestureHandlerRootView>
  );
}
